import type { FC } from 'react'

import { useEffect, useState } from 'react'
import { Club, Diamond, Heart, Spade } from 'lucide-react'
import { cn } from '@/lib/utils'

export type Suit = 'spades' | 'hearts' | 'diamonds' | 'clubs'

export type CardValue = {
    rank: number
    suit: Suit
}

export const SUITS: Suit[] = ['spades', 'hearts', 'diamonds', 'clubs']

const RANK_LABELS = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K']

const SUIT_ICONS = {
    spades: Spade,
    hearts: Heart,
    diamonds: Diamond,
    clubs: Club
}

const SIZES = {
    sm: { card: 'h-[44px] w-[32px] rounded-[6px] p-1', rank: 'text-[9px]', pip: 'size-3.5' },
    md: { card: 'h-[68px] w-[48px] rounded-[9px] p-1.5', rank: 'text-[11px]', pip: 'size-5' },
    lg: { card: 'h-[100px] w-[72px] rounded-[12px] p-2', rank: 'text-[15px]', pip: 'size-8' }
}

export const rankLabel = (rank: number): string => RANK_LABELS[rank - 1] ?? '?'

export const drawCard = (): CardValue => ({
    rank: 1 + Math.floor(Math.random() * 13),
    suit: SUITS[Math.floor(Math.random() * SUITS.length)]
})

interface PlayingCardProps {
    card: CardValue | null
    faceUp: boolean
    size?: keyof typeof SIZES
    highlight?: 'win' | 'dim' | null
    className?: string
}

interface DealtCardProps extends Omit<PlayingCardProps, 'faceUp'> {
    hidden?: boolean
}

const PlayingCard: FC<PlayingCardProps> = ({ card, faceUp, size = 'md', highlight = null, className }) => {
    const styles = SIZES[size]
    const red = card?.suit === 'hearts' || card?.suit === 'diamonds'
    const Icon = card ? SUIT_ICONS[card.suit] : Spade
    const label = card ? `${rankLabel(card.rank)} of ${card.suit}` : 'Face down card'

    return (
        <div
            role='img'
            aria-label={faceUp ? label : 'Face down card'}
            className={cn('relative shrink-0 perspective-[800px] transition-opacity duration-300', styles.card, 'p-0', highlight === 'dim' && 'opacity-45', className)}
        >
            <div className={cn('relative size-full transition-transform duration-500 ease-[cubic-bezier(0.3,1.25,0.45,1)] transform-3d', faceUp ? 'rotate-y-0' : 'rotate-y-180')}>
                <div
                    className={cn(
                        'absolute inset-0 flex flex-col justify-between border bg-bone shadow-[0_14px_30px_-14px_rgba(0,0,0,0.9)] backface-hidden',
                        styles.card,
                        highlight === 'win' ? 'border-ember ring-2 ring-ember/60' : 'border-black/10'
                    )}
                >
                    <span className={cn('font-mono font-semibold leading-none', styles.rank, red ? 'text-[#D2361C]' : 'text-ink')}>
                        {card ? rankLabel(card.rank) : ''}
                    </span>
                    <Icon
                        aria-hidden='true'
                        strokeWidth={1.25}
                        className={cn('self-center', styles.pip, red ? 'fill-[#D2361C] text-[#D2361C]' : 'fill-ink text-ink')}
                    />
                    <span className={cn('rotate-180 self-end font-mono font-semibold leading-none', styles.rank, red ? 'text-[#D2361C]' : 'text-ink')}>
                        {card ? rankLabel(card.rank) : ''}
                    </span>
                </div>

                <div className={cn('absolute inset-0 rotate-y-180 overflow-hidden border border-white/10 bg-ink-3 backface-hidden', styles.card)}>
                    <div className='absolute inset-[3px] rounded-[inherit] border border-ember/30 bg-[repeating-linear-gradient(45deg,rgba(255,91,31,0.16)_0_2px,transparent_2px_7px)]' />
                </div>
            </div>
        </div>
    )
}

/** A card that arrives face down and turns over a moment later. */
export const DealtCard: FC<DealtCardProps> = ({ hidden = false, ...props }) => {
    const [turned, setTurned] = useState(false)

    useEffect(() => {
        const id = window.setTimeout(() => setTurned(true), 90)
        return () => window.clearTimeout(id)
    }, [])

    return (
        <div className='animate-in fade-in slide-in-from-top-6 duration-300'>
            <PlayingCard {...props} faceUp={turned && !hidden} />
        </div>
    )
}

export default PlayingCard
